import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Trash2, X } from "lucide-react";

type DeleteMonthlyDemandDialogProps = {
  monthLabel: string;
  busy: boolean;
  labels?: {
    eyebrow: string;
    title: string;
    body: string;
    cancel: string;
    confirm: string;
    busy: string;
    close: string;
  };
  onCancel: () => void;
  onConfirm: () => void;
};

const defaultLabels = {
  eyebrow: "Delete monthly demand",
  title: "Delete this monthly demand?",
  body: "This permanently removes the product, brand and category selections saved for this month. This action cannot be undone.",
  cancel: "Keep demand",
  confirm: "Delete demand",
  busy: "Deleting…",
  close: "Close delete confirmation",
};

export default function DeleteMonthlyDemandDialog({ monthLabel, busy, labels = defaultLabels, onCancel, onConfirm }: DeleteMonthlyDemandDialogProps) {
  const titleId = useId();
  const cancelButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [busy, onCancel]);

  return createPortal(
    <div className="delete-demand-dialog" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <section className="delete-demand-dialog__window" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={`${titleId}-description`}>
        <button type="button" className="delete-demand-dialog__close" disabled={busy} onClick={onCancel} aria-label={labels.close}><X /></button>
        <span className="delete-demand-dialog__icon" aria-hidden="true"><AlertTriangle /></span>
        <span className="delete-demand-dialog__eyebrow">{labels.eyebrow}</span>
        <h2 id={titleId}>{labels.title}</h2>
        <strong className="delete-demand-dialog__month">{monthLabel}</strong>
        <p id={`${titleId}-description`}>{labels.body}</p>
        <div className="delete-demand-dialog__actions">
          <button ref={cancelButton} type="button" className="button button--ghost" disabled={busy} onClick={onCancel}>{labels.cancel}</button>
          <button type="button" className="delete-demand-dialog__confirm" disabled={busy} onClick={onConfirm}><Trash2 />{busy ? labels.busy : labels.confirm}</button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
